import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Shield, ChevronDown, Check } from 'lucide-react';
import { useBU } from './BUContext';

const BU_META = {
  AFFILIATE: { label: 'AFFILIATE', sub: 'Performance Marketing', Icon: Briefcase },
  ABH: { label: 'ABH', sub: 'AfterBody Health', Icon: Shield },
};

const WorkspaceSwitcher = ({ user }) => {
  const bu = useBU();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Founder-only + must have access to more than one BU
  if (user?.role !== 'FOUNDER' || !bu.isMultiBU || !bu.currentBU) return null;

  const current = BU_META[bu.currentBU] || { label: bu.currentBU, sub: '', Icon: Briefcase };
  const CurrentIcon = current.Icon;

  const choose = (code) => {
    setOpen(false);
    if (bu.currentBU === code) return;
    bu.setCurrentBU(code);
    navigate('/dashboard');
  };

  return (
    <div
      ref={rootRef}
      data-testid="bu-switcher"
      className="px-4 py-3 border-b border-slate-800 relative"
    >
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1.5">Workspace</p>
      <button
        type="button"
        data-testid="bu-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="w-full flex items-center gap-2.5 bg-slate-950/60 hover:bg-slate-950 border border-slate-800 hover:border-slate-700 rounded-lg px-3 py-2 text-left transition-colors"
      >
        <span className="w-7 h-7 rounded-md bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
          <CurrentIcon className="w-4 h-4" strokeWidth={2} />
        </span>
        <span className="flex-1 min-w-0">
          <span
            data-testid="bu-switcher-current-label"
            className="block text-sm font-semibold text-white leading-tight"
          >
            {current.label}
          </span>
          {current.sub && (
            <span className="block text-[11px] text-slate-500 leading-tight truncate">
              {current.sub}
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <ul
          data-testid="bu-switcher-menu"
          role="listbox"
          className="absolute left-4 right-4 mt-1.5 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl shadow-black/40 overflow-hidden"
        >
          {bu.access.map((code) => {
            const meta = BU_META[code] || { label: code, sub: '', Icon: Briefcase };
            const Icon = meta.Icon;
            const active = bu.currentBU === code;
            return (
              <li key={code}>
                <button
                  type="button"
                  data-testid={`bu-switch-${code.toLowerCase()}`}
                  onClick={() => choose(code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                    active ? 'bg-emerald-500/10' : 'hover:bg-slate-800'
                  }`}
                >
                  <span
                    className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                      active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span
                      className={`block text-sm font-medium leading-tight ${
                        active ? 'text-white' : 'text-slate-200'
                      }`}
                    >
                      {meta.label}
                    </span>
                    {meta.sub && (
                      <span className="block text-[11px] text-slate-500 leading-tight truncate">
                        {meta.sub}
                      </span>
                    )}
                  </span>
                  {active && <Check className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
